/**
 * 标签页布局组件
 * 支持在当前页面内打开多个节点，使用标签页进行管理
 */
import React, { Suspense, useState, useLayoutEffect, useCallback, useRef, useMemo, useEffect } from 'react';
import { Tabs, Dropdown, Input, Avatar, Tooltip, Breadcrumb } from 'antd';
import type { TabsProps } from 'antd';
import {
  CloseOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  UserOutlined,
  SunOutlined,
  MoonOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { history, useLocation, useModel, FormattedMessage, SelectLang as UmiSelectLang, getIntl, useOutlet, useAppData } from '@umijs/max';
// eslint-disable-next-line no-restricted-imports -- 与 umi 同一份 react-router 实例,用于给缓存页签钉住 location
import { UNSAFE_LocationContext } from 'react-router';
import { flushSync } from 'react-dom';
import { HOME_ENTRY_PATH, isHomeEntryPath, normalizeNavigationPath, resolveHomePath } from '@/utils/homeNavigation';
import { outLogin } from '@/services/ant-design-pro/api';
import { KeepAliveActiveContext, KeepAlivePrevActiveKeyContext } from '@/hooks/keepAliveActive';
import { AppShellContext } from '@/hooks/appShell';
import ContentLoading from '@/components/ContentLoading';
// tagsview 页签栏元数据统一走 sessionStorage（F5 保留、关浏览器清空），
// 读写/清理收口在 @/utils/tagsViewStorage，便于各登出路径复用同一份 key。
import {
  clearTagsViewTabs,
  loadTagsViewTabs,
  saveTagsViewTabs,
} from '@/utils/tagsViewStorage';
import styles from './index.less';
import CustomerEntryReminder from './CustomerEntryReminder';
import { isSameTabModule } from './moduleKey';
import { finishPageLoadingAfterPaint, startPageLoading } from '@/utils/pageLoading';
// ⚠️ 临时导航性能探针，排查结束后一并删除
import { navPerf } from '@/utils/navPerfProbe';
import { applyDarkModeAttribute, persistDarkMode } from '@/utils/theme';
import { publishDarkMode, useDarkMode } from '@/utils/darkMode';
// ⚠️ 临时探针（主题切换专项），排查结束后连同 themeSwitchProbe.ts 一起删除
import { themeSwitchProbe } from './themeSwitchProbe';

const THEME_PROBE_ON = process.env.NODE_ENV !== 'production';

/**
 * ── 主题切换的「同帧落定」守卫 ─────────────────────────────────────────
 *
 * 翻色本身是同一帧发生的（CSS 变量随 <html data-theme> 一起翻），但**各模块
 * 自己的颜色过渡时长并不统一**，于是翻完之后它们各花各的时间才到位：
 *   · 侧栏 .ant-pro-sider / .ant-layout-sider  transition: all .2s（为折叠动画写的）
 *   · 顶栏 .breadcrumbRow                      transition: background-color .2s, border-color .2s
 *   · 页签条 / 内容区 / 表格                    无过渡 → 瞬间到位
 * 0ms / 150ms / 200ms / 300ms 混在一起，用户看到的就是
 * 「页面的几个大模块各自切换」。切换的那一帧给 <html> 挂上 .theme-flipping
 * （规则见 src/global.less）全站禁过渡，所有面同帧落定。
 *
 * ⚠️ 只禁 transition、不禁 animation —— 启动动画与登录页动效素材不受影响。
 * ⚠️ 不是第二套主题标记：它不参与判色，只是切换期的动画闸门。
 */
const THEME_FLIP_CLASS = 'theme-flipping';
/** 代次：连续快速切换时，让上一轮的收尾失效，避免把新那轮的守卫提前摘掉 */
let themeFlipSeq = 0;
let themeFlipTimer: number | undefined;

function beginThemeFlip(): void {
  if (typeof document === 'undefined') return;
  themeFlipSeq += 1;
  if (themeFlipTimer !== undefined) {
    window.clearTimeout(themeFlipTimer);
    themeFlipTimer = undefined;
  }
  document.documentElement.classList.add(THEME_FLIP_CLASS);
}

/**
 * 守卫必须在「翻转已经绘制」之后才摘 —— 摘早了浏览器做样式重算时读不到
 * 禁过渡标记，过渡照样会跑。
 *
 * 因此用**双 rAF**：rAF 回调在「该帧样式重算之前」执行，单次 rAF 会在主题色
 * 第一次被重算前就把标记摘掉；第二次 rAF 落在「主题已绘制」之后。
 * 定时器只是 rAF 被节流（切到后台标签页 / 该帧无渲染）时的兜底，不参与常规时序。
 */
function endThemeFlipAfterPaint(): void {
  if (typeof document === 'undefined') return;
  const seq = themeFlipSeq;
  const clear = () => {
    if (seq !== themeFlipSeq) return; // 已被下一次切换接管
    if (themeFlipTimer !== undefined) {
      window.clearTimeout(themeFlipTimer);
      themeFlipTimer = undefined;
    }
    document.documentElement.classList.remove(THEME_FLIP_CLASS);
  };
  requestAnimationFrame(() => requestAnimationFrame(clear));
  themeFlipTimer = window.setTimeout(clear, 400);
}

export interface TabItem {
  key: string;
  label: string;
  path: string;
  component?: React.ReactNode;
  closable?: boolean;
}

interface TabLayoutProps {
  children?: React.ReactNode;
}

/** 页签重挂提交后交接给异步 chunk 与页面请求，销毁时释放本次刷新。 */
const CacheEntryLoadingCommit: React.FC<{
  endLoading?: () => void;
  active: boolean;
}> = ({ endLoading, active }) => {
  useLayoutEffect(() => {
    if (!endLoading) return undefined;
    if (!active) {
      endLoading();
      return undefined;
    }
    return finishPageLoadingAfterPaint(endLoading);
  }, [endLoading, active]);
  return null;
};

/**
 * 财务页刷新时权限菜单可能尚未装载，不能把父级 menu.finance 当成子节点页签名。
 * 这些路径与 finance/routes.ts 一一对应，作为初始化阶段的稳定标题源；
 * 菜单装载完成后仍由数据库菜单负责侧边栏和面包屑。
 */
const FINANCE_TAB_LABELS: Record<string, string> = {
  '/finance/deposit-application': '登船押金申请单',
  '/finance/business-trip-application': '出差申请单',
  '/finance/cash-advance-application': '借支申请单',
  '/finance/customer-management-fee-application': '客户管理费申请单',
  '/finance/entertainment-expense-application': '业务招待费用申请单',
  '/finance/purchase-prepayment-application': '采购预付申请单',
  '/finance/payable-order': '应付单',
  '/finance/payment-order': '付款单',
  '/finance/receivable-order': '应收单',
  '/finance/collection-order': '收款单',
  '/finance/sales-prepayment-order': '销售预收单',
};

/**
 * 显式「打开/激活页签」导航（单据联查、审批中心跳单据等深链专用；普通导航仍用 history.push）。
 * 与普通 push 的差别，仅对被标记的这一次导航生效：
 * 1. 目标模块已有页签且当前在其他模块时，普通 push 会被下方「保留深层 tab」的菜单返回
 *    语义 history.replace 打回旧路径（联查表现为点了没反应）；显式打开跳过该语义。
 * 2. 目标 path+query 的缓存页签已存在时，缓存页钉住创建时的 location，对本次导航零感知；
 *    显式打开会 refresh 该页签（整棵重挂 → 页面的 searchParams 深链 effect 重跑）。
 * 页签缓存、切换、关闭等其余语义不变。
 */
let pendingTabOpen: string | null = null;
export const openTab = (fullPath: string) => {
  pendingTabOpen = normalizeNavigationPath(fullPath);
  history.push(fullPath);
};

const TabLayout: React.FC<TabLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [activeKey, setActiveKey] = useState<string>('');
  const [tabs, setTabs] = useState<TabItem[]>(() => {
    // 登录/注册页不恢复历史页签（登出跳转或直接刷新登录页时保持干净）
    if (
      location.pathname.startsWith('/user/login') ||
      location.pathname.startsWith('/user/register')
    ) {
      return [];
    }
    // F5 刷新后从 sessionStorage 恢复页签栏：只恢复标签元数据，
    // 各页签内容由下方 keep-alive 按当前 location 重新挂载；
    // label 直接复用存储值（刷新后菜单数据尚未异步装载，重新解析会落到英文兜底）。
    return loadTagsViewTabs<TabItem>().map((t) => ({
      key: normalizeNavigationPath(String(t.path)),
      label: String(t.label ?? ''),
      path: normalizeNavigationPath(String(t.path)),
      closable: t.closable !== false,
    }));
  });
  const activeKeyRef = useRef<string>('');
  const aliveRef = useRef<{
    destroy: (keyOrKeys: string | string[]) => void;
    destroyOther: (keep: string) => void;
    refresh: (key: string) => void;
  } | null>(null); // 页签缓存操作句柄（自研 keep-alive，见下）
  const pendingRedirectRef = useRef<string | null>(null); // 保留深层 tab 路径时的重定向标记

  /**
   * ── 页签级 keep-alive（自研，2026-07-02 替换 keepalive-for-react 运行时）──
   *
   * 为什么不用库（两个都被 jsdom 复现证实）：
   * 1. 库给每个页签存的 children 快照若是活路由出口（childrenRender 的 children），
   *    所有缓存节点都会渲染成「当前页」→ 开 N 个页签切一次页 = 当前列表页同时挂 N 份、
   *    连发 N 个相同列表查询（单据页初始化十几连发 + 全站卡顿的根因）。
   * 2. 改喂 useOutlet() 冻结元素后，库在 startTransition 里注册缓存节点，
   *    遇 React.lazy 页面 chunk 未就绪的 suspend，该 transition 不自行提交
   *    → 新开页签一直空白，直到下一次导航才补渲染。
   *
   * 此实现全同步、元素冻结、身份稳定：
   * - 新页签：渲染期 setState 注册（React 支持 render-phase 自更新），Suspense 正常出
   *   fallback → chunk 就绪显内容，无空白；
   * - 切页签：仅 display 切换，页面组件不重挂、不重发请求、状态/滚动保留；
   * - 缓存元素身份恒定 → TabLayout 重渲染时各页签子树整体 bail-out，互不陪跑。
   */
  const outlet = useOutlet();
  const cacheKey = normalizeNavigationPath(location.pathname + location.search);
  // ⚠️ 临时探针：本次渲染起点，用于量渲染+提交总耗时（排查结束后删除）
  const __perfMark = navPerf.tabRenderStart();
  type KaEntry = { key: string; ele: React.ReactElement; ver: number; refreshEnd?: () => void };
  const [kaEntries, setKaEntries] = useState<KaEntry[]>([]);
  const kaEntriesRef = useRef(kaEntries);
  kaEntriesRef.current = kaEntries;
  const kaRefreshPendingRef = useRef(new Map<string, () => void>());
  const kaLruRef = useRef<Map<string, number>>(new Map());
  const kaSeqRef = useRef(0);
  // openTab 显式深链标记：location 已到达目标地址的首次渲染时消费，
  // 记录目标 path+query 当时是否已有缓存页签（决定后续是否需要 refresh 重挂）
  const explicitOpenRef = useRef<{ key: string; hadEntry: boolean } | null>(null);
  if (pendingTabOpen !== null) {
    explicitOpenRef.current =
      pendingTabOpen === cacheKey
        ? { key: pendingTabOpen, hadEntry: kaEntries.some((e) => e.key === pendingTabOpen) }
        : null; // 未落到目标地址（被路由改写等）：丢弃标记，避免误伤后续导航
    pendingTabOpen = null;
  }
  // 登录/注册页不进缓存（同步按 location 判定）
  const isAuthPage = location.pathname.startsWith('/user/login') || location.pathname.startsWith('/user/register');
  /**
   * ⚠️ 重定向路由（如菜单裸路径 /inquiry-quote/purchase-inquiry → redirect 到 ./list）
   * 只能「活渲染」让 <Navigate> 执行跳转，绝不能缓存：react-router 的 Navigate 每次
   * 重渲染都会强制跳转，一旦被冻结进隐藏缓存节点，就变成永久劫持器——之后点任何页签
   * URL 都被拽回它的目标，页面 chunk 请求被中断（ERR_ABORTED）、骨架屏卡死
   * （真机 Chrome + CDP 复现证实，正是"只有第一个页签能开"的根因）。
   * clientRoutes 会剥掉 redirect 属性，故用 useAppData().routes 原始路由表建集合。
   */
  const appData = useAppData();
  const redirectPaths = useMemo(() => {
    const s = new Set<string>();
    const rs: Record<string, any> = (appData as any)?.routes || {};
    Object.keys(rs).forEach((k) => {
      const r = rs[k];
      if (r?.redirect) s.add((r.absPath || r.path || '').replace(/\/+$/, '') || '/');
    });
    return s;
  }, [appData]);
  const normPath = normalizeNavigationPath(location.pathname);
  const isRedirectPath = redirectPaths.has(normPath) || isHomeEntryPath(normPath);
  kaLruRef.current.set(cacheKey, ++kaSeqRef.current);
  // 记录「上一个激活的缓存页 key」——列表页据此区分「从本模块详情页返回」(刷新) 与
  // 「从其他页签切回」(保持)。渲染期同步更新，早于子树 effect 读取，值恒准确。
  const kaPrevActiveKeyRef = useRef<string | null>(null);
  const kaCurActiveKeyRef = useRef<string>(cacheKey);
  if (kaCurActiveKeyRef.current !== cacheKey) {
    kaPrevActiveKeyRef.current = kaCurActiveKeyRef.current;
    kaCurActiveKeyRef.current = cacheKey;
  }
  // 稳定身份的访问器：作为 context value 不会变化，故不触发任何缓存页重渲染。
  const kaGetPrevActiveKey = useRef(() => kaPrevActiveKeyRef.current).current;
  const kaContent = outlet ?? children;
  /**
   * ⚠️ 每个缓存条目必须「钉住」自己创建时的 LocationContext：
   * useLocation/useSearchParams 订阅的是活 context——不钉住的话，切页签时【隐藏的】
   * 编辑/新增页会看到别人的 URL（无 id/mode），其按参数驱动的 effect 被误触发，
   * 表单被重置甚至整个编辑视图被卸载 → 切回后手填/修改全丢
   * （真机复现：新增页手填→切页签→输入框 DOM 直接消失）。
   * 钉住后隐藏页对外部导航零感知，也不再陪跑重渲染。
   * UNSAFE_LocationContext 与 umi 同一份 react-router 实例（node_modules 顶层唯一 6.3）。
   */
  const locationCtxValue = React.useContext(UNSAFE_LocationContext as any);
  if (!isAuthPage && !isRedirectPath && kaContent && !kaEntries.some((e) => e.key === cacheKey)) {
    // 渲染期注册：同步生效，无 effect 时序缝隙（首帧即有缓存节点可显示）
    setKaEntries((prev) => {
      if (prev.some((e) => e.key === cacheKey)) return prev;
      let next = prev;
      const MAX = 20;
      if (next.length >= MAX) {
        // 开放页签当前显示的页面可能持有未保存草稿，不能因浏览其他单据而卸载。
        // 仅淘汰节点内部的历史页面；开放节点超过上限时允许缓存暂时超过 MAX。
        const openPaths = new Set(tabs.map((tab) => tab.path));
        openPaths.add(cacheKey);
        const lru = next.filter((entry) => !openPaths.has(entry.key)).sort(
          (a, b) => (kaLruRef.current.get(a.key) || 0) - (kaLruRef.current.get(b.key) || 0),
        )[0];
        if (lru) next = next.filter((e) => e !== lru);
      }
      const Provider = (UNSAFE_LocationContext as any).Provider;
      return [
        ...next,
        {
          key: cacheKey,
          ele: (
            <Provider value={locationCtxValue}>
              <React.Fragment>{kaContent}</React.Fragment>
            </Provider>
          ),
          ver: 0,
        },
      ];
    });
  }
  const kaDestroy = useCallback((keyOrKeys: string | string[]) => {
    const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
    setKaEntries((prev) => prev.filter((entry) => !keys.some((key) => (
      entry.key === key || isSameTabModule(entry.key, key)
    ))));
  }, []);
  const kaDestroyOther = useCallback((keep: string) => {
    setKaEntries((prev) => prev.filter((entry) => (
      entry.key === keep || isSameTabModule(entry.key, keep)
    )));
  }, []);
  const kaRefresh = useCallback((key: string) => {
    if (!kaEntriesRef.current.some((entry) => entry.key === key)) return;
    // 连续刷新各持有自己的结束句柄，旧组件 cleanup 不会提前结束新刷新。
    kaRefreshPendingRef.current.get(key)?.();
    const end = startPageLoading('正在刷新页面', { immediate: true });
    const refreshEnd = () => {
      end();
      if (kaRefreshPendingRef.current.get(key) === refreshEnd) {
        kaRefreshPendingRef.current.delete(key);
      }
    };
    kaRefreshPendingRef.current.set(key, refreshEnd);
    // ver+1 → React key 变化 → 该页签整棵重挂（列表重新拉取），其余页签不动
    setKaEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ver: e.ver + 1, refreshEnd } : e)));
  }, []);
  // ⚠️ 临时探针：提交后上报本次渲染+提交耗时（布局 effect 在全树提交后、绘制前运行）
  // 同时上报 document 节点数与缓存页签数，用于验证「DOM 累积导致提交变贵」的假设。
  useLayoutEffect(() => {
    if (__perfMark == null) return;
    navPerf.tabCommit(
      __perfMark,
      performance.now(),
      document.getElementsByTagName('*').length,
      kaEntriesRef.current.length,
    );
  });

  aliveRef.current = { destroy: kaDestroy, destroyOther: kaDestroyOther, refresh: kaRefresh };

  useLayoutEffect(() => {
    // 刷新与关闭/导航同一批发生时，未提交的条目也必须释放。
    for (const [key, end] of kaRefreshPendingRef.current) {
      const entry = kaEntries.find((item) => item.key === key);
      if (key !== cacheKey || entry?.refreshEnd !== end) end();
    }
  }, [kaEntries, cacheKey]);
  useEffect(() => {
    const pending = kaRefreshPendingRef.current;
    return () => { for (const end of pending.values()) end(); };
  }, []);

  // 搜索相关状态
  const [searchText, setSearchText] = useState('');

  // 菜单数据版本号：__MENU_DATA__ 由 app.tsx 在渲染期写入 window（非 React 状态），
  // 首次就绪时 bump 一次，驱动依赖它的 useMemo/页签 label 修复 effect 重算。
  const [menuDataVersion, setMenuDataVersion] = useState(0);
  useEffect(() => {
    if (!(window as any).__MENU_DATA__?.length) return;
    setMenuDataVersion((v) => v + 1);
  }, []);

  // 获取当前用户信息
  const { initialState, setInitialState } = useModel('@@initialState');
  const currentUser = initialState?.currentUser;
  const homePath = resolveHomePath(currentUser?.homePage);
  const homeName = currentUser?.homePage?.name || '工作台';
  const homeTab = useMemo<TabItem>(() => ({ key: homePath, path: homePath, label: homeName, closable: false }), [homePath, homeName]);
  // 主题来自 utils/darkMode 的 store（不写 initialState：那会让整棵 @@initialState
  // 树重渲染，包括 umi 的 layout 运行时 —— 见该文件说明）
  const darkMode = useDarkMode();
  const sidebarCollapsed = initialState?.sidebarCollapsed || false;

  // 切换侧边栏折叠/展开
  const toggleSidebar = useCallback(() => {
    setInitialState((prev: any) => ({
      ...prev,
      sidebarCollapsed: !prev?.sidebarCollapsed,
    }) as any);
  }, [setInitialState]);

  // 同步 <html> 上的 data-theme，确保 Portal 弹出层也能感知暗色模式。
  // 历史上同时挂在 body 上的 dark-mode class 已废弃（样式层零消费）：
  // 暗色统一由 <html data-theme="dark"> 驱动，不再维护第二套标记。
  useLayoutEffect(() => {
    // 同步实现收在 utils/theme.ts（亮色必须 removeAttribute，不能只写不管清）
    applyDarkModeAttribute(darkMode);
  }, [darkMode]);

  // 处理从侧边栏菜单切回同模块时，保留深层 tab（如详情页）的重定向
  useEffect(() => {
    if (pendingRedirectRef.current) {
      const path = pendingRedirectRef.current;
      pendingRedirectRef.current = null;
      history.replace(path);
    }
  });

  // 切换主题模式
  const toggleTheme = useCallback(() => {
    const newDarkMode = !darkMode;
    /**
     * 三件事落在**同一帧**，顺序不能换（否则观感就是「几个大模块各自切换」）：
     *
     *   1. 先挂同帧落定守卫（.theme-flipping，规则见 global.less）：同一任务内的两次
     *      属性写入之间不会发生样式重算，所以守卫与翻色被浏览器一起读进同一次
     *      「变更后样式」；先挂是防御性顺序 —— 万一将来中间插进了会强制同步重算的
     *      读取（getComputedStyle 之类），守卫仍在翻色之前就已生效。
     *   2. 再同步写 <html data-theme>（persistDarkMode 内部做）：CSS 变量面本帧翻。
     *      这一步必须在事件回调里同步做，不能只依赖下面那个 useLayoutEffect ——
     *      那要等 React 提交，翻转会被拆成两拍（CSS 变量面等 effect、React 面等提交），
     *      观感就是「部分先变、部分延迟」。useLayoutEffect 保留作为首帧兜底校准。
     *   3. 最后广播给订阅者：navTheme/ProLayout token、页签条、看板图表这些
     *      **必须走 JS 值**的面跟上（离散事件里的提交本就在本任务内、绘制之前）。
     *
     * 广播只通知真正消费主题的组件；不写 initialState，所以不会牵动全站重渲染。
     * antd 组件那一层完全不吃这一步：算法固定为亮色，暗色由 styles/antdDarkTokens.css
     * 在 `[data-theme="dark"]` 下覆盖 --ant-* 变量，属性一翻它就跟着翻。
     */
    if (THEME_PROBE_ON) themeSwitchProbe.begin();
    beginThemeFlip();
    // 落盘 + 同步根属性都收在 utils/theme.ts（全站唯一实现，避免判据再漂）
    persistDarkMode(newDarkMode);
    publishDarkMode(newDarkMode);

    // 提交已返回（离散事件里 setState 在本任务内提交完），此处起量「首帧」；
    // 守卫等这一帧画完再摘。
    if (THEME_PROBE_ON) themeSwitchProbe.end(kaEntriesRef.current.length);
    endThemeFlipAfterPaint();
  }, [darkMode]);

  // Tooltip 弹出层样式：适配亮色/暗色主题（跟随主题，与 Dropdown 一致）
  const tooltipOverlayStyle = useMemo(() => ({
    backgroundColor: darkMode
      ? 'rgba(0, 0, 0, 0.75)'
      : 'rgba(255, 255, 255, 0.9)',
    color: darkMode ? '#fff' : '#000',
  }), [darkMode]);

  // 退出登录
  const loginOut = useCallback(async () => {
    try {
      await outLogin();
    } catch (error) {
      console.error('登出失败:', error);
    } finally {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      clearTagsViewTabs(); // 登出即清空页签持久化数据，重新登录从干净状态开始

      setInitialState((prev) => ({ ...prev, currentUser: undefined, userMenus: undefined }));
      setKaEntries([]);
      setTabs([]);
      activeKeyRef.current = '';
      setActiveKey('');
      window.dispatchEvent(new CustomEvent('logout'));
      // 主动退出不继承旧页面；会话过期登录仍由 401 流程保留深链。
      history.replace('/user/login');
    }
  }, [setInitialState, tabs]);

  // 根据路径获取菜单标题
  const getTabLabelMemo = useCallback((pathWithoutQuery: string): string => {
    const normalizedPath =
      pathWithoutQuery.length > 1
        ? pathWithoutQuery.replace(/\/+$/, '')
        : pathWithoutQuery;
    if (normalizedPath === homePath) return homeName;
    if (normalizedPath === '/workbench') return '工作台';
    const financeLabel = FINANCE_TAB_LABELS[normalizedPath];
    if (financeLabel) return financeLabel;

    const findMenuLabelRecursively = (menus: any[], targetPath: string): string | null => {
      if (!menus) return null;
      for (const menu of menus) {
        // 一级菜单（有子菜单时）不设 path，但 key 就是它的 path（见 app.tsx convertBackendMenusToProLayout）
        // 兼容按 key 匹配，让一级菜单段也能命中 DB 菜单名，而不是落到 locale 兜底
        if (menu.path === targetPath || menu.key === targetPath) {
          let label = menu.name || menu.label || null;
          if (label && typeof label === 'string' && label.startsWith('menu.')) {
            try {
              const intl = (window as any).__REACT_INTL__;
              if (intl && intl.formatMessage) {
                const translatedLabel = intl.formatMessage({ id: label }, label);
                return translatedLabel;
              }
            } catch (e) {
              // 如果获取失败，使用原始值
            }
          }
          return label;
        }
        if (menu.children && menu.children.length > 0) {
          const found = findMenuLabelRecursively(menu.children, targetPath);
          if (found) return found;
        }
        if (menu.routes && menu.routes.length > 0) {
          const found = findMenuLabelRecursively(menu.routes, targetPath);
          if (found) return found;
        }
      }
      return null;
    };

    // 1. 首先尝试从经过权限和名称处理的菜单数据中查找（包含父级继承的名称）
    const menuData = (window as any).__MENU_DATA__ || [];
    let label = findMenuLabelRecursively(menuData, pathWithoutQuery);
    if (label) {
      return label;
    }

    // 2. 如果在处理过的菜单数据中找不到，尝试从原始路由配置中查找
    const routeMenuData = (window as any).__ROUTE_MENU_DATA__ || [];
    label = findMenuLabelRecursively(routeMenuData, normalizedPath);
    // 命中的 name 若是英文 slug（如 shipment-notice），不能直接定稿——
    // 它只是路由标识不是显示名，直接返回会短路下方 i18n 层
    // （如 /logistics/shipment/notice/list → menu.logistics.shipment.notice
    //   → 发货通知单），导致页签/面包屑显示英文。留作兜底，i18n 也没命中时再返回。
    const routeSlugFallback = label || null;
    if (label && /[\u4e00-\u9fa5]/.test(label)) {
      return label;
    }
    
    // 3. 用 i18n 菜单 locale 解析：按路径段构造 menu.* key（locale 键与路径层级一致，
    //    如 /procurement/purchase-order → menu.procurement.purchase-order）。
    //    修复后端菜单未命中（含末尾斜杠 /xxx/、菜单数据未加载完）时页签显示英文编码的问题。
    //    filter(Boolean) 已天然丢弃末尾斜杠产生的空段。
    const pathParts = pathWithoutQuery.split('/').filter(Boolean);
    try {
      const intl = getIntl();
      if (intl && intl.formatMessage && pathParts.length > 0) {
        // 从最深层逐级回退（子路由命不中时退回父级菜单名）
        for (let end = pathParts.length; end >= 1; end--) {
          const id = 'menu.' + pathParts.slice(0, end).join('.');
          const translated = intl.formatMessage({ id });
          if (translated && translated !== id) {
            return translated;
          }
        }
      }
    } catch (e) {
      // 忽略，落到标题化兜底
    }

    // 3.5 i18n 也没命中：返回路由 slug（保持原有「路由名优先」兜底，无中文时零回归）
    if (routeSlugFallback) {
      return routeSlugFallback;
    }

    // 4. 仍找不到：标题化最后一段（通用回退）
    const lastPart = pathParts[pathParts.length - 1] || '首页';
    const fallbackLabel = lastPart
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return fallbackLabel;
  }, [homePath, homeName]);

  // 根据路径查找一级菜单 key（有子菜单才返回，用于面包屑点击打开抽屉）
  const findMenuKeyByPath = (path: string): string | null => {
    const menuData = (window as any).__MENU_DATA__ || [];
    for (const menu of menuData) {
      if ((menu.path === path || menu.key === path) && menu.children && menu.children.length > 0) {
        return menu.key || null;
      }
    }
    return null;
  };

  // 面包屑导航（基于路径分段逐级查找，复用 getTabLabelMemo 的多层兜底逻辑）
  const breadcrumbItems = useMemo(() => {
    const items: { title: React.ReactNode; key: string }[] = [];
    const pathname = normPath;
    // 跳过根路径
    if (pathname === '/' || pathname === '') return items;
    // 首项：首页，点击回到系统默认页
    items.push({
      title: <a onClick={() => history.push(HOME_ENTRY_PATH)}>首页</a>,
      key: '/',
    });
    // 客户进仓单归属物流菜单，兼容原仓库 URL；其余页面仍按路径段构建。
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbPaths = pathname === '/warehouse/customer-entry-notice'
      ? ['/logistics', pathname]
      : segments.map((_, index) => '/' + segments.slice(0, index + 1).join('/'));
    breadcrumbPaths.forEach((accumulated, idx) => {
      const label = getTabLabelMemo(accumulated);
      const isLast = idx === breadcrumbPaths.length - 1;
      if (isLast) {
        items.push({ title: <span>{label}</span>, key: accumulated });
      } else if (idx === 0 && breadcrumbPaths.length > 1) {
        // 一级菜单段：点击打开抽屉展示下级菜单，而非直接跳转
        const menuKey = findMenuKeyByPath(accumulated);
        if (menuKey) {
          items.push({
            title: (
              <a onClick={() => window.dispatchEvent(new CustomEvent('menu:openDrawer', { detail: { key: menuKey } }))}>
                {label}
              </a>
            ),
            key: accumulated,
          });
        } else {
          items.push({
            title: <a onClick={() => history.push(accumulated)}>{label}</a>,
            key: accumulated,
          });
        }
      } else {
        items.push({
          title: <a onClick={() => history.push(accumulated)}>{label}</a>,
          key: accumulated,
        });
      }
    });
    return items;
  }, [normPath, getTabLabelMemo]);

  // 搜索菜单 - 扁平化菜单数据（每次渲染从全局变量实时读取，避免 useMemo 缓存过期）
  const flatMenuList = useMemo(() => {
    const menuData: any[] = (typeof window !== 'undefined')
      ? ((window as any).__MENU_DATA__ || [])
      : [];
    const flatten = (menus: any[], result: any[] = []) => {
      if (!menus) return result;
      for (const menu of menus) {
        if (menu.name && menu.path) {
          result.push({ name: menu.name, path: menu.path, key: menu.key });
        }
        if (menu.children && menu.children.length > 0) {
          flatten(menu.children, result);
        }
      }
      return result;
    };
    return flatten(menuData);
  }, [menuDataVersion]);

  // 过滤搜索结果
  const filteredMenus = useMemo(() => {
    if (!searchText.trim()) return [];
    const keyword = searchText.toLowerCase();
    return flatMenuList.filter(
      (item: any) =>
        item.name?.toLowerCase().includes(keyword) ||
        item.path?.toLowerCase().includes(keyword),
    ).slice(0, 20); // 最多显示20条
  }, [searchText, flatMenuList]);

  /**
   * 页签名兜底修复：自定义菜单渲染是在首次渲染期把 __MENU_DATA__ 写进 window 的，
   * 而页签 useLayoutEffect 在子组件 effect 之前执行——首帧读不到菜单，只能落到
   * 标题化兜底（英文）。因此菜单数据首次就绪时重算一次已有页签的 label。
   */
  useEffect(() => {
    setTabs((prevTabs) => {
      let changed = false;
      const next = prevTabs.map((t) => {
        const label = getTabLabelMemo(t.path.split('?')[0]);
        if (!label || label === t.label) return t;
        changed = true;
        return { ...t, label };
      });
      return changed ? next : prevTabs;
    });
  }, [menuDataVersion, getTabLabelMemo]);

  // 点击搜索结果跳转
  const handleMenuClick = (path: string) => {
    setSearchText('');
    history.push(path);
  };

  // 首页页签只固定元数据，不预加载首页业务，直接访问业务深链仍停在原页面。
  useLayoutEffect(() => {
    if (!currentUser || isAuthPage) return;
    setTabs((previous) => {
      const others = previous.filter((tab) => tab.path !== homePath && !isHomeEntryPath(tab.path))
        .map((tab) => tab.closable === false ? { ...tab, closable: true } : tab);
      const next = [homeTab, ...others];
      return previous.length === next.length && previous.every((tab, index) => (
        tab.path === next[index].path && tab.label === next[index].label && tab.closable === next[index].closable
      )) ? previous : next;
    });
  }, [currentUser, isAuthPage, homePath, homeTab]);

  // 监听路由变化，添加或激活标签页
  useLayoutEffect(() => {
    const currentPath = cacheKey; // 页面身份忽略末尾斜杠，保留全部查询参数
    const pathWithoutQuery = normPath;
    // openTab 显式深链标记：先捕获成局部值再给 setTabs 的 updater 用——updater 在
    // 之后的重渲染阶段才执行，届时 ref 已被下方收尾逻辑清空，直接读 ref 会失效
    const explicitOpen = explicitOpenRef.current?.key === currentPath ? explicitOpenRef.current : null;

    // 跳过登录页等不需要标签页的页面
    if (currentPath.startsWith('/user/login') || currentPath.startsWith('/user/register')) {
      setTabs([]);
      activeKeyRef.current = '';
      setActiveKey('');
      // 进入登录页即清理页签持久化数据（登出/会话过期后换账号不串页签）
      clearTagsViewTabs();
      // keep-alive 缓存独立于 tabs、不会自动销毁；不清的话上一账号缓存的页面
      // 会继续挂在 DOM 里。用 ref 判断，避免每次依赖变化都 setState 出新的空数组。
      if (kaEntriesRef.current.length) {
        aliveRef.current?.destroy(kaEntriesRef.current.map((e) => e.key));
      }
      return;
    }

    if (isRedirectPath) return;

    // ⚠️ updater 必须保持纯函数：React 可能重复调用（StrictMode 下必双调用），而菜单标题
    // 查表读的是 window 上的菜单快照，属外部读取 —— 故在 updater 之外算好再传入。
    const currentTabLabel = getTabLabelMemo(pathWithoutQuery);

    setTabs(prevTabs => {
      const existingIndex = prevTabs.findIndex(t => {
        const tabPathWithoutQuery = t.path.split('?')[0];
        return tabPathWithoutQuery === pathWithoutQuery && (t.path !== homePath || currentPath === homePath);
      });

      if (activeKeyRef.current === currentPath && existingIndex >= 0) {
        const existingTab = prevTabs[existingIndex];
        if (existingTab.path === currentPath && existingTab.key === currentPath) {
          return prevTabs;
        }
      }

      if (existingIndex >= 0) {
        const existingTab = prevTabs[existingIndex];
        if (existingTab.path === currentPath && existingTab.key === currentPath) {
          if (activeKeyRef.current !== currentPath) {
            activeKeyRef.current = currentPath;
            setActiveKey(currentPath);
          }
          return prevTabs;
        }
        
        const updatedTabs = [...prevTabs];
        updatedTabs[existingIndex] = { 
          ...existingTab, 
          path: currentPath, 
          key: currentPath 
        };
        activeKeyRef.current = currentPath;
        setActiveKey(currentPath);
        return updatedTabs;
      } else {
        const sameModuleIndex = prevTabs.findIndex(t => {
          return isSameTabModule(t.path, currentPath) && (t.path !== homePath || currentPath === homePath);
        });
        
        if (sameModuleIndex >= 0) {
          const existingTab = prevTabs[sameModuleIndex];
          const existingCleanPath = existingTab.path.split('?')[0];

          // 🔑 同模块但路径不同时，判断是「模块内导航」还是「从其他模块切回」：
          //    - 模块内导航（列表→详情）：activeKey 在同一模块，允许正常更新 tab
          //    - 跨模块切回（侧边栏菜单点击）：activeKey 在其他模块，保留深层 tab 并重定向
          if (existingCleanPath !== pathWithoutQuery) {
            const isReturningFromOtherModule =
              // openTab 显式深链（单据联查等）必须落到目标地址，不走「保留深层 tab」回跳
              !explicitOpen &&
              activeKeyRef.current &&
              !isSameTabModule(activeKeyRef.current, currentPath);
            if (isReturningFromOtherModule) {
              activeKeyRef.current = existingTab.key;
              setActiveKey(existingTab.key);
              pendingRedirectRef.current = existingTab.path;
              return prevTabs;
            }
          }

          // 路径相同或模块内导航，正常更新 tab
          const updatedTabs = [...prevTabs];
          updatedTabs[sameModuleIndex] = {
            key: currentPath,
            label: currentTabLabel,
            path: currentPath,
            closable: pathWithoutQuery !== homePath,
          };
          activeKeyRef.current = currentPath;
          setActiveKey(currentPath);
          return updatedTabs;
        }
        
        const newTab: TabItem = {
          key: currentPath,
          label: currentTabLabel,
          path: currentPath,
          closable: pathWithoutQuery !== homePath,
        };
        activeKeyRef.current = currentPath;
        setActiveKey(currentPath);
        return [...prevTabs, newTab];
      }
    });

    // openTab 显式深链收尾：目标 path+query 已有缓存页签时重挂它（页面 searchParams
    // 深链 effect 重跑，如 ?openId= 打开详情）；新建缓存的页签首挂天然读新 query，无需处理。
    if (explicitOpen) {
      if (explicitOpen.hadEntry) aliveRef.current?.refresh(currentPath);
      explicitOpenRef.current = null;
    }
  }, [cacheKey, normPath, getTabLabelMemo, homePath, isRedirectPath]);

  /**
   * 页签栏元数据写入 sessionStorage（会话级：F5 刷新恢复，关浏览器标签页自动清空）
   *
   * ⚠️ 必须带「已登录 + 非登录/注册页」守卫，否则会出现
   *    「登出时清空了、随后又被写回去」的竞态：
   *
   *    · 清存储 clearTagsViewTabs() 是命令式调用，立即生效；
   *    · 清状态 setTabs([]) 是异步 state 更新，要等下一次渲染才生效。
   *    两者之间存在窗口期，而本 effect 是 tabs 引用的**无条件**副作用 ——
   *    只要窗口期里有任何 effect 让 tabs 换了引用，旧页签就会被原样写回
   *    sessionStorage。而这样的「生产者」确实存在：下面「页签名兜底修复」
   *    effect 依赖 menuDataVersion，登出会清 currentUser/userMenus、
   *    菜单随后重新装载，它就会重算页签并返回新数组。
   *
   *    加了守卫后：登出瞬间 currentUser 即被清空 → 本 effect 直接不写，
   *    无论各 effect 以什么顺序执行，旧页签都不可能再落盘。
   */
  const isAuthRoute =
    location.pathname.startsWith('/user/login') ||
    location.pathname.startsWith('/user/register');

  useEffect(() => {
    if (!currentUser || isAuthRoute) return;
    // 存储失败（隐私模式/配额）不影响页签功能，已由 saveTagsViewTabs 内部兜底
    saveTagsViewTabs(tabs.map((t) => ({
      key: t.key,
      label: t.label,
      path: t.path,
      closable: t.closable,
    })));
  }, [tabs, currentUser, isAuthRoute]);

  // 切换标签页
  const handleTabChange = (key: string) => {
    const tab = tabs.find(t => t.key === key);
    if (tab) {
      setActiveKey(key);
      // 保留完整的路径和查询参数，确保状态不丢失
      const fullPath = tab.path; // tab.path 已经包含了查询参数
      if (fullPath !== cacheKey) {
        history.push(fullPath);
      }
    }
  };

  // 关闭标签页
  const handleTabRemove = (targetKey: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    
    const targetIndex = tabs.findIndex(tab => tab.key === targetKey);
    if (targetIndex < 0 || tabs[targetIndex].path === homePath) return;

    const newTabs = tabs.filter(tab => tab.key !== targetKey);
    setTabs(newTabs);
    // keep-alive：关页签即销毁缓存（重开=全新列表态）
    aliveRef.current?.destroy(targetKey);

    if (targetKey === activeKey && newTabs.length > 0) {
      const nextTab = newTabs[targetIndex] || newTabs[targetIndex - 1] || newTabs[0];
      if (nextTab) {
        setActiveKey(nextTab.key);
        history.push(nextTab.path);
      }
    }

    if (newTabs.length === 0) {
      history.push(HOME_ENTRY_PATH);
    }
  };

  // 关闭其他标签页
  const handleCloseOthers = (targetKey: string) => {
    const targetTab = tabs.find(t => t.key === targetKey);
    if (!targetTab) return;

    aliveRef.current?.destroyOther(targetKey); // 销毁除保留页签外的缓存
    setTabs(currentUser && targetTab.path !== homePath ? [homeTab, targetTab] : [targetTab]);
    setActiveKey(targetKey);
    history.push(targetTab.path);
  };

  // 关闭所有标签页
  const handleCloseAll = () => {
    setKaEntries([]); // 包括已不在页签当前路径中的列表、详情和编辑历史缓存
    kaLruRef.current.clear();
    setTabs(currentUser ? [homeTab] : []);
    setActiveKey(homePath);
    history.push(HOME_ENTRY_PATH);
  };

  // 刷新指定标签页（只刷新当前页签，不刷新整个页面）
  const handleReload = (tabKey: string) => {
    const targetTab = tabs.find(t => t.key === tabKey);
    if (!targetTab) return;
    if (tabKey !== activeKey) {
      setActiveKey(tabKey);
      history.replace(targetTab.path);
    }
    // keep-alive：refresh 该页签缓存 → 组件重挂、列表重新拉取（手动刷新）
    aliveRef.current?.refresh(tabKey);
  };

  // 右键菜单
  const getTabMenu = (tabKey: string) => {
    return {
      items: [
        {
          key: 'reload',
          label: '刷新',
          icon: <ReloadOutlined />,
          onClick: () => handleReload(tabKey),
        },
        {
          key: 'close-others',
          label: '关闭其他',
          onClick: () => handleCloseOthers(tabKey),
        },
        {
          key: 'close-all',
          label: '关闭所有',
          icon: <CloseCircleOutlined />,
          onClick: handleCloseAll,
        },
      ],
    };
  };

  const tabItems: TabsProps['items'] = tabs.map(tab => ({
    key: tab.key,
    label: (
      <Dropdown menu={getTabMenu(tab.key)} trigger={['contextMenu']}>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {tab.label}
        </span>
      </Dropdown>
    ),
    closable: tab.path !== homePath, // 使用 Ant Design Tabs 自带的 closable，会自动显示一个 ×
    // 内容不在 antd Tabs 面板里渲染，改由下方单个 KeepAlive 统一承载（切走不卸载、切回原样恢复）
    children: null,
  }));

  // 工具栏：标签栏右侧固定区域
  // （原「布局密度」按钮已移除：密度改由屏幕档位自动派生，
  //   见 hooks/useDensity.ts + constants/screenTier.ts）
  const toolbarRef = useRef<HTMLDivElement>(null);
  const toolbarContent = (
    <div className={styles.toolbar} ref={toolbarRef}>
      <div className={styles.searchInput}>
        <Input
          placeholder="搜索菜单"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          maxLength={20}
        />
        {searchText.trim() && (
          <div className={styles.searchDropdown}>
            {filteredMenus.length > 0 ? (
              filteredMenus.map((item: any) => (
                <div
                  key={item.key}
                  className={styles.searchItem}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleMenuClick(item.path);
                    
                  }}
                >
                  <span className={styles.searchItemName}>{item.name}</span>
                </div>
              ))
            ) : (
              <div className={styles.searchEmpty}>未找到匹配的菜单</div>
            )}
          </div>
        )}
      </div>
      <span className={styles.toolbarItem}>
        <UmiSelectLang
          style={{
            padding: 0,
          }}
          // @ts-ignore getPopupContainer 会通过 restProps 转发至 antd Dropdown
          getPopupContainer={() => toolbarRef.current || document.body}
        />
      </span>
      <Tooltip title={darkMode ? '切换亮色模式' : '切换暗色模式'} styles={{ body: tooltipOverlayStyle }}>
        <span className={styles.toolbarItem} onClick={toggleTheme}>
          {darkMode ? <SunOutlined /> : <MoonOutlined />}
        </span>
      </Tooltip>
      <CustomerEntryReminder user={currentUser} refreshKey={location.pathname + location.search}
        className={styles.toolbarItem} onOpen={() => openTab('/warehouse/customer-entry-notice?pending=1')} />
      <Dropdown
        menu={{
          items: [
            {
              key: 'logout',
              icon: <LogoutOutlined />,
              label: '退出系统',
              onClick: () => {
                flushSync(() => {
                  setInitialState((s) => ({ ...s, currentUser: undefined }));
                });
                loginOut();
              },
            },
          ],
        }}
        placement="bottomRight"
      >
        <span className={styles.userItem}>
          <Avatar
            size={24}
            src={currentUser?.avatar}
            icon={<UserOutlined />}
            style={{ marginRight: 4 }}
          />
          <span className={styles.userName}>{currentUser?.name || ''}</span>
        </span>
      </Dropdown>
    </div>
  );

  // 面包屑导航行（折叠按钮 + 面包屑左 + 工具栏右）
  const breadcrumbRow = (
    <div className={styles.breadcrumbRow}>
      <div className={styles.collapseBtn} onClick={toggleSidebar}>
        {sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </div>
      <div className={styles.breadcrumbArea}>
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className={styles.toolbarSpacer} />
      {toolbarContent}
    </div>
  );

  // 始终使用 Tabs 包裹内容，保持 DOM 结构不变，避免结构切换导致的布局跳动
  const showTabBar = tabs.length > 0;

  // 无标签栏时，使用单 page 承载 children
  const finalTabItems = showTabBar
    ? tabItems
    : [{ key: '_page', label: '', children: null, closable: false } as any];

  // ⚠️ 临时探针：函数体结束打点，把 render 阶段从提交耗时里拆出来（排查结束后删除）
  navPerf.tabRenderEnd(__perfMark);

  return (
    <div className={styles.tabLayout}>
      {breadcrumbRow}
      <Tabs
        type="editable-card"
        activeKey={showTabBar ? activeKey : '_page'}
        items={finalTabItems as TabsProps['items']}
        onChange={showTabBar ? handleTabChange : undefined}
        onEdit={(targetKey, action) => {
          if (action === 'remove') {
            handleTabRemove(targetKey as string);
          }
        }}
        hideAdd
        className={showTabBar ? styles.tabs : `${styles.tabs} ${styles.tabsNoNav}`}
      />
      {/* 页签内容：自研 keep-alive（实现与动机见组件顶部注释）。
          每个页签一个缓存节点，display 切换显隐；元素身份恒定，隐藏页签不陪跑不发请求。 */}
      <div className={styles.tabContent}>
        {!isAuthPage ? (
          <div className={styles.kaContainer}>
           {/* ⚠️ 缓存节点区域 = 「外壳已挂载」。umi 给每个路由组件套的 Suspense
               fallback（src/loading.tsx）落在这里面，靠这个信号决定只占内容区，
               否则它会铺满全屏、把侧边栏和页签栏一起盖掉。 */}
           <AppShellContext.Provider value={true}>
           <KeepAlivePrevActiveKeyContext.Provider value={kaGetPrevActiveKey}>
            {kaEntries.map((e) => (
              <div
                key={`${e.key}#${e.ver}`}
                className={styles.kaNode}
                style={e.key === cacheKey ? undefined : { display: 'none' }}
              >
                {/* 注入「激活」信号：value 为稳定布尔基元，未切页签时各节点值不变、
                    不触发任何 consumer 重渲染，保留「隐藏页不陪跑」不变量；
                    切页签时仅新旧激活两节点跳变，列表页据此 reload（见 useListRefresh 场景 1）。*/}
                <KeepAliveActiveContext.Provider value={e.key === cacheKey}>
                  {/* 兜底 Suspense：umi 自己那层（在 e.ele 内部）才是路由 chunk 的
                      第一道边界，这里只是保证任何其它 suspend 也不会把外壳带走。 */}
                  <Suspense fallback={<ContentLoading />}>{e.ele}</Suspense>
                  <CacheEntryLoadingCommit endLoading={e.refreshEnd} active={e.key === cacheKey} />
                </KeepAliveActiveContext.Provider>
              </div>
            ))}
            {/* 重定向路径：活渲染（不缓存），让 <Navigate> 正常执行跳转 */}
            {isRedirectPath && (
              <div key="__redirect_live__" className={styles.kaNode}>
                <Suspense fallback={<ContentLoading />}>{kaContent}</Suspense>
              </div>
            )}
           </KeepAlivePrevActiveKeyContext.Provider>
           </AppShellContext.Provider>
          </div>
        ) : (
          <div style={{ height: '100%', overflow: 'auto' }}>{children}</div>
        )}
      </div>
    </div>
  );
};

export default TabLayout;
